import {
  Activity,
  Bell,
  BarChart3,
  Bot,
  Box,
  Building2,
  CalendarDays,
  Check,
  CircleCheckBig,
  CircleHelp,
  ClipboardList,
  Cog,
  Columns3,
  Compass,
  CornerDownRight,
  Crosshair,
  Database,
  DollarSign,
  Drill,
  FileCheck,
  FileStack,
  FileText,
  Flag,
  Gauge,
  Hammer,
  HardHat,
  Headset,
  LayoutGrid,
  Lightbulb,
  LineChart,
  Link2,
  ListTree,
  ListChecks,
  Map,
  MapPin,
  Mountain,
  Package,
  Receipt,
  RefreshCw,
  Route,
  RotateCw,
  ScrollText,
  Search,
  ShieldCheck,
  Signal,
  Stethoscope,
  Timer,
  Target,
  TrendingUp,
  TriangleAlert,
  Truck,
  Users,
  Warehouse,
  Waypoints,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { BreadcrumbSegment } from '@/shared/types';
import {
  hasCount,
  OEM_STATIC_COUNTS,
  showsExecutiveBadge,
  type OemCounts,
} from './oem-counts';
// fork(P3c): the site crumb reads the site's display name.
import { MINES } from '../data/core';
// fork(P5b): loop display names for the `/loops/:loopId` crumb.
import { LOOP_BY_ID } from './seed/loops';
// fork(P5a): the two dynamic workflow crumbs read their label from the seed.
import { MODULE_BY_ID } from './seed/modules';
import { WORKFLOW_STEP_BY_ID } from './seed/workflow-steps';
// fork(L3): the generated-report crumb reads the division's name.
import { isWeeklyKind, WEEKLY_DIVISION } from './report-catalog';

// =========================================================================
// OEM+ navigation, as data. Fork-owned (zone C).
//
// The demo drives its left nav from 269 inline `onclick="show(view, sub)"`
// strings. This module is the single replacement: every section, every item,
// its route, its tier and its badge live here, so the second-pass IA
// simplification is a config edit rather than a markup rewrite.
//
// Tiers (plan §Tiers):
//   1  default path, polished — what the Komatsu demo walks through
//   2  faithful but off the default path — collapsed sections
//   3  roadmap only — never rendered while TIER_MAX is 2
// Flipping TIER_MAX to 1 hides every Tier 2 item in one line.
//
// Icons are lucide picks matched to the demo's hand-drawn glyphs; section
// order and item order follow the demo markup (lines 1352-1419) exactly.
// =========================================================================

export type NavTier = 1 | 2 | 3;

/** Highest tier rendered in the sidebar and searched by the palette. */
export const TIER_MAX: NavTier = 2;

/**
 * How much of the build the left rail admits — simplification pass A1.
 *
 * Orthogonal to {@link NavTier} on purpose. Tier says how finished a surface
 * is; mode says whether an aftermarket executive should meet it in the rail.
 * `TIER_MAX` therefore keeps meaning exactly what it meant, and nothing is
 * unrouted in either mode: every surface stays reachable by URL, by ⌘K and
 * from in-page links.
 *
 * @see ./DemoModeContext.tsx — the provider, the storage key and the default
 */
export type DemoMode = 'executive' | 'full';

/**
 * The executive rail, as an **ordered** allowlist of {@link OemNavItem} ids.
 *
 * Order here is the order the rows render in executive mode, which is the
 * order the story is told in rather than the order the demo's markup happened
 * to list: the landing, then the two outcome numbers, then where they are,
 * then how the work gets done, then who decides.
 *
 * Section membership and section order still come from {@link OEM_SECTIONS} —
 * this list only says which rows survive and how they sort inside a section.
 * An id that no row claims is inert, so a future retier cannot silently
 * shorten the rail.
 *
 * Labels and placement are deliberately unchanged from full mode. The IA
 * questions in `docs/plans/simplification-pass.md` §4 (rename Workflows, move
 * Approvals under Issues, merge Customers and Mine sites) are open with Jay,
 * and this pass does not pre-empt them.
 */
export const EXECUTIVE_NAV_IDS: readonly string[] = [
  // HOME — the landing, the two outcomes, the footprint, how the work gets
  // done, and who decides.
  'asset360',
  'costs',
  'fleet-health',
  'priorities',
  'sites-map',
  'workflows',
  'integration',
  'approvals',

  // ISSUES — predictive maintenance, second in order, as agreed.
  'issues-live',
  'alerts',
  'incidents',

  // MINE SITES — collapsed by default; six rows behind one heading.
  'site-KG',
  'site-BR',
  'site-MI',
  'site-OH',
  'site-GD',
  'site-MR',
];

/**
 * id → position in {@link EXECUTIVE_NAV_IDS}. Membership test and sort key in
 * one. A plain object rather than a `Map` because this module imports lucide's
 * `Map` icon, which shadows the global.
 */
const EXECUTIVE_RANK: Readonly<Record<string, number>> = Object.fromEntries(
  EXECUTIVE_NAV_IDS.map((id, index) => [id, index] as const),
);

/** Does this nav id survive the executive cut? */
export function isExecutiveNavId(id: string): boolean {
  return Object.prototype.hasOwnProperty.call(EXECUTIVE_RANK, id);
}

/**
 * Sections whose heading stays open on first paint in executive mode — A7.
 *
 * `defaultOpen: true` on Workflows expands 37 rows before the reader has done
 * anything, which is most of what makes the rail read as busy. Stated as an
 * allowlist rather than inherited from {@link OEM_SECTIONS} so the answer to
 * "what is open when the room first sees this?" is one line, and so a future
 * section cannot arrive open by default without someone typing its id here.
 *
 * `OemSidebar` forces a section open regardless when it holds the current
 * route, so a deep link never lands on a page whose own row is hidden.
 */
const EXECUTIVE_OPEN_SECTIONS: readonly OemSectionId[] = ['home', 'issues'];

export type OemSectionId =
  | 'home'
  | 'issues'
  | 'workflows'
  | 'loops'
  | 'assets'
  | 'sites'
  | 'integration'
  | 'help';

export type OemNavItem = {
  /** Stable id; also the palette key and the nav-id comment anchor slug. */
  id: string;
  label: string;
  /** Absolute route. Must match a route literal in app/router.tsx. */
  to: string;
  icon: LucideIcon;
  tier: NavTier;
  /**
   * Trailing count pill, **resolved at render time** by
   * {@link navBadge} against `oem-counts.ts`.
   *
   * A row opts in by having an entry in `OEM_STATIC_COUNTS` under its `id` —
   * there is no second key to keep in step. Set `badge` here only to pin a
   * literal that no count derives (nothing does today); a pinned literal
   * wins over the derived figure.
   *
   * P2 shipped these as hard-coded numbers with a `TODO(p1-merge)`. P3a
   * replaced every one with a derivation over `data/core.ts` and the seeds;
   * see `oem-counts.ts` for which two are still fallbacks and why.
   */
  badge?: number;
  /** Renders the pill in the critical treatment (demo `.n.hot`). */
  hot?: boolean;
  /** Indented child row — the demo's `button.sub`. */
  sub?: boolean;
  /**
   * Rendered in executive mode only — fork(P7a/A2).
   *
   * Two rows exist solely because the executive rail hides the section that
   * owns them: `/workflows` and `/integration` are section landings, and with
   * the Workflows and Integration headings gone there is nothing else to
   * click. In full mode both headings are right there, so a duplicate row
   * under Home would be noise. These rows are also kept out of
   * {@link NAV_BY_PATH}, so breadcrumbs read identically in both modes.
   */
  executiveOnly?: boolean;
  /** `docs/logic/<module>/<slug>` for the surface's HelpAnchor. */
  helpDoc?: string;
};

export type OemNavSection = {
  id: OemSectionId;
  label: string;
  icon: LucideIcon;
  /** Demo sections carrying `class="nsec closed"` start collapsed. */
  defaultOpen: boolean;
  items: OemNavItem[];
};

// -------------------------------------------------------------------------
// Sections
// -------------------------------------------------------------------------

export const OEM_SECTIONS: OemNavSection[] = [
  {
    id: 'home',
    label: 'Home',
    icon: Crosshair,
    defaultOpen: true,
    items: [
      { id: 'asset360', label: 'Asset360', to: '/', icon: Crosshair, tier: 1, helpDoc: 'asset360/overview' },
      { id: 'sites-map', label: 'Sites map', to: '/sites', icon: Map, tier: 1, helpDoc: 'sites/map' },
      { id: 'customers', label: 'Customers', to: '/customers', icon: Building2, tier: 2, helpDoc: 'customers/index' },
      { id: 'job-board', label: 'Job board', to: '/job-board', icon: Columns3, tier: 2, helpDoc: 'workflows/job-board' },
      { id: 'fleet-health', label: 'Fleet health', to: '/fleet', icon: LineChart, tier: 1, helpDoc: 'fleet/health' },
      // fork(oem/v1.8): the A$ driver tree — reviewer feedback, "which machine to target first".
      { id: 'priorities', label: 'Priorities', to: '/priorities', icon: Target, tier: 1, helpDoc: 'fleet/priorities' },
      { id: 'sos-report', label: 'S.O.S report', to: '/reports/sos', icon: FileText, tier: 2, helpDoc: 'reports/sos' },
      // fork(L3): the reports index — six generated documents. Tier 2, so it
      // is in the Full rail only; the executive rail stays as it is.
      { id: 'reports', label: 'Reports', to: '/reports', icon: FileStack, tier: 2, helpDoc: 'reports/overview' },
      { id: 'approvals', label: 'Approvals', to: '/approvals', icon: Check, tier: 1, hot: true, helpDoc: 'approvals/queue' },
      { id: 'costs', label: 'Cost summary', to: '/costs', icon: DollarSign, tier: 1, helpDoc: 'costs/summary' },
      // fork(P7a/A2): the two section landings the executive rail needs
      // because it drops their headings. Executive mode only — see
      // `executiveOnly` on OemNavItem. Labels are the existing section
      // labels: the renames in the plan's §4 (D5, D6) are open with Jay.
      {
        id: 'workflows',
        label: 'Workflows',
        to: '/workflows',
        icon: Workflow,
        tier: 1,
        executiveOnly: true,
        helpDoc: 'workflows/section',
      },
      {
        id: 'integration',
        label: 'Integration',
        to: '/integration',
        icon: Link2,
        tier: 1,
        executiveOnly: true,
        helpDoc: 'integration/overview',
      },
    ],
  },
  {
    id: 'issues',
    label: 'Issues',
    icon: TriangleAlert,
    defaultOpen: true,
    items: [
      { id: 'issues-live', label: 'Live', to: '/issues', icon: Activity, tier: 1, hot: true, helpDoc: 'issues/live' },
      { id: 'alerts', label: 'Alerts', to: '/issues/alerts', icon: Bell, tier: 1, hot: true, helpDoc: 'issues/alerts' },
      { id: 'incidents', label: 'Incidents', to: '/issues/incidents', icon: Timer, tier: 1, helpDoc: 'issues/incidents' },
      // fork(oem/v1.8): root cause → the person who acts, and operator training needs.
      // Tier 2 and out of EXECUTIVE_NAV_IDS — the executive rail stays lean; the
      // alert page and Issues Live link here.
      { id: 'issues-routing', label: 'Routing & training', to: '/issues/routing', icon: Waypoints, tier: 2, helpDoc: 'issues/routing' },
    ],
  },
  {
    id: 'workflows',
    label: 'Workflows',
    icon: Workflow,
    defaultOpen: true,
    items: [
      // 10 operating-model modules, each followed by its workflow steps.
      { id: 'module-installed', label: '01 Asset Base', to: '/workflows/modules/installed', icon: Truck, tier: 2, helpDoc: 'workflows/module-installed' },
      { id: 'step-reg', label: 'Installed base register', to: '/workflows/steps/reg', icon: ListChecks, tier: 2, sub: true },
      { id: 'step-life', label: 'Machine life & meters', to: '/workflows/steps/life', icon: Gauge, tier: 2, sub: true },

      { id: 'module-demand', label: '02 Customer & Asset Needs', to: '/workflows/modules/demand', icon: Signal, tier: 2, helpDoc: 'workflows/module-demand' },
      { id: 'step-duty', label: 'Duty & demand signals', to: '/workflows/steps/duty', icon: Activity, tier: 2, sub: true },
      { id: 'step-forecast', label: 'Predicted parts demand', to: '/workflows/steps/forecast', icon: TrendingUp, tier: 2, sub: true },

      { id: 'module-orchestrate', label: '03 Case Management', to: '/workflows/modules/orchestrate', icon: ClipboardList, tier: 2, helpDoc: 'workflows/module-orchestrate' },
      { id: 'step-intake', label: 'Case intake & triage', to: '/workflows/steps/intake', icon: CornerDownRight, tier: 2, sub: true },
      { id: 'case-trace', label: 'Case-to-closure trace', to: '/workflows/case-trace', icon: Route, tier: 2, sub: true, helpDoc: 'workflows/case-trace' },

      { id: 'module-diagnose', label: '04 Assess & Prescribe', to: '/workflows/modules/diagnose', icon: Search, tier: 2, helpDoc: 'workflows/module-diagnose' },
      { id: 'step-assess', label: 'Diagnosis & prescription', to: '/workflows/steps/assess', icon: Stethoscope, tier: 2, sub: true },
      { id: 'step-rca', label: 'Root cause analysis', to: '/workflows/steps/rca', icon: Compass, tier: 2, sub: true },
      { id: 'step-remote4', label: 'Remote diagnosis', to: '/workflows/steps/remote4', icon: Headset, tier: 2, sub: true },

      { id: 'module-commercial', label: '05 Commercial & Entitlement', to: '/workflows/modules/commercial', icon: FileCheck, tier: 2, helpDoc: 'workflows/module-commercial' },
      { id: 'step-entitle', label: 'Entitlement & warranty', to: '/workflows/steps/entitle', icon: ShieldCheck, tier: 2, sub: true },
      { id: 'step-value', label: 'Quote, margin & growth', to: '/workflows/steps/value', icon: Receipt, tier: 2, sub: true },

      { id: 'module-promise', label: '06 Service Planning', to: '/workflows/modules/promise', icon: CalendarDays, tier: 2, helpDoc: 'workflows/module-promise' },
      { id: 'step-plan', label: 'Service plan & window', to: '/workflows/steps/plan', icon: CalendarDays, tier: 2, sub: true },
      { id: 'step-ready', label: 'Promise & readiness', to: '/workflows/steps/ready', icon: CircleCheckBig, tier: 2, sub: true },

      { id: 'module-parts', label: '07 Parts & Supply', to: '/workflows/modules/parts', icon: Package, tier: 2, helpDoc: 'workflows/module-parts' },
      { id: 'step-pipe', label: 'Pipeline & expediting', to: '/workflows/steps/pipe', icon: Route, tier: 2, sub: true },
      { id: 'step-stock', label: 'Stock & replenishment', to: '/workflows/steps/stock', icon: Warehouse, tier: 2, sub: true },
      { id: 'step-book', label: 'Parts book & supersessions', to: '/workflows/steps/book', icon: ScrollText, tier: 2, sub: true },

      { id: 'module-capacity', label: '08 People & Resources', to: '/workflows/modules/capacity', icon: Users, tier: 2, helpDoc: 'workflows/module-capacity' },
      { id: 'step-crew', label: 'Capacity & utilisation', to: '/workflows/steps/crew', icon: BarChart3, tier: 2, sub: true },
      { id: 'step-skills', label: 'Skills & certification', to: '/workflows/steps/skills', icon: HardHat, tier: 2, sub: true },

      { id: 'module-execute', label: '09 Execution & Support', to: '/workflows/modules/execute', icon: Cog, tier: 2, helpDoc: 'workflows/module-execute' },
      { id: 'step-pack', label: 'Work package execution', to: '/workflows/steps/pack', icon: Hammer, tier: 2, sub: true },
      { id: 'step-onsite', label: 'On-site time capture', to: '/workflows/steps/onsite', icon: Timer, tier: 2, sub: true },

      { id: 'module-closeout', label: '10 Closeout & Value', to: '/workflows/modules/closeout', icon: CircleCheckBig, tier: 2, helpDoc: 'workflows/module-closeout' },
      { id: 'step-cost', label: 'Job costing & cost to serve', to: '/workflows/steps/cost', icon: DollarSign, tier: 2, sub: true },
      { id: 'step-outcome', label: 'Customer outcome & CSS', to: '/workflows/steps/outcome', icon: TrendingUp, tier: 2, sub: true },

      // Operational surfaces the demo reaches from inside pages rather than
      // from its own left nav. The plan's Tier 2 route table groups them here.
      { id: 'job-cards', label: 'Job cards', to: '/job-cards', icon: ClipboardList, tier: 2, helpDoc: 'workflows/job-cards' },
      { id: 'parts-flow', label: 'Parts flow', to: '/parts-flow', icon: Package, tier: 2, helpDoc: 'workflows/parts-flow' },
      { id: 'technicians', label: 'Technicians', to: '/technicians', icon: Wrench, tier: 2, helpDoc: 'workflows/technicians' },
      { id: 'remote-support', label: 'Remote support', to: '/remote-support', icon: Headset, tier: 2, helpDoc: 'workflows/remote-support' },
      { id: 'population', label: 'Population', to: '/population', icon: BarChart3, tier: 2, helpDoc: 'workflows/population' },
    ],
  },
  {
    id: 'loops',
    label: 'Feedback loops',
    icon: RefreshCw,
    defaultOpen: false,
    items: [
      { id: 'loop-component', label: 'Component Lifecycle & Rebuild', to: '/loops/component', icon: RotateCw, tier: 2, helpDoc: 'loops/component' },
      { id: 'loop-warranty', label: 'Warranty, Quality & Campaigns', to: '/loops/warranty', icon: ShieldCheck, tier: 2, helpDoc: 'loops/warranty' },
      { id: 'loop-learning', label: 'Fleet Learning & Continuous Improvement', to: '/loops/learning', icon: Lightbulb, tier: 2, helpDoc: 'loops/learning' },
      { id: 'loop-commercial', label: 'Commercial Opportunity Management', to: '/loops/commercial', icon: TrendingUp, tier: 2, helpDoc: 'loops/commercial' },
      { id: 'loop-planning', label: 'Demand & Supply Planning', to: '/loops/planning', icon: BarChart3, tier: 2, helpDoc: 'loops/planning' },
    ],
  },
  {
    id: 'assets',
    label: 'Assets',
    icon: Box,
    defaultOpen: true,
    items: [
      { id: 'division-surface', label: 'Surface Drilling', to: '/assets/surface', icon: Mountain, tier: 2, helpDoc: 'fleet/division' },
      { id: 'subclass-top-hammer', label: 'Top-hammer drills', to: '/assets/surface?subclass=Top-hammer%20drills', icon: Drill, tier: 2, sub: true },
      { id: 'subclass-blasthole', label: 'Blasthole drills', to: '/assets/surface?subclass=Blasthole%20drills', icon: Drill, tier: 2, sub: true },

      { id: 'division-drilling', label: 'Underground Drilling', to: '/assets/drilling', icon: Drill, tier: 2, helpDoc: 'fleet/division' },
      { id: 'subclass-development', label: 'Development drills', to: '/assets/drilling?subclass=Development%20drills', icon: Drill, tier: 2, sub: true },
      { id: 'subclass-production', label: 'Production drills', to: '/assets/drilling?subclass=Production%20drills', icon: Drill, tier: 2, sub: true },

      { id: 'division-loadhaul', label: 'Underground Load & Haul', to: '/assets/loadhaul', icon: Truck, tier: 2, helpDoc: 'fleet/division' },
      { id: 'subclass-loaders', label: 'Loaders', to: '/assets/loadhaul?subclass=Loaders', icon: Truck, tier: 2, sub: true },
      { id: 'subclass-haulage', label: 'Haulage trucks', to: '/assets/loadhaul?subclass=Haulage%20trucks', icon: Truck, tier: 2, sub: true },
    ],
  },
  {
    id: 'sites',
    label: 'Mine sites',
    icon: MapPin,
    defaultOpen: false,
    items: [
      { id: 'site-KG', label: 'Kalbarri Gold', to: '/sites/KG', icon: MapPin, tier: 1, helpDoc: 'sites/mine' },
      { id: 'site-BR', label: 'Broken Ridge', to: '/sites/BR', icon: MapPin, tier: 1, helpDoc: 'sites/mine' },
      { id: 'site-MI', label: 'Mount Isolde', to: '/sites/MI', icon: MapPin, tier: 1, helpDoc: 'sites/mine' },
      { id: 'site-OH', label: 'Ochre Hill Gold', to: '/sites/OH', icon: MapPin, tier: 1, helpDoc: 'sites/mine' },
      { id: 'site-GD', label: 'Gawler Deep', to: '/sites/GD', icon: MapPin, tier: 1, helpDoc: 'sites/mine' },
      { id: 'site-MR', label: 'Mount Read Metals', to: '/sites/MR', icon: MapPin, tier: 1, helpDoc: 'sites/mine' },
    ],
  },
  {
    id: 'integration',
    label: 'Integration',
    icon: Link2,
    defaultOpen: true,
    items: [
      { id: 'data', label: 'Data', to: '/integration/data', icon: Database, tier: 2, helpDoc: 'integration/data' },
      { id: 'connectors', label: 'Connectors', to: '/connectors', icon: Link2, tier: 2, helpDoc: 'integration/connectors' },
      { id: 'agents', label: 'Agents', to: '/agents', icon: Bot, tier: 2, helpDoc: 'integration/agents' },
    ],
  },
  {
    id: 'help',
    label: 'Help',
    icon: CircleHelp,
    defaultOpen: false,
    items: [
      { id: 'help-map', label: 'Platform map', to: '/help/platform-map', icon: Map, tier: 2, helpDoc: 'help/platform-map' },
      // fork(oem/v1.8): every section and sub-page as one numbered outline.
      { id: 'help-structure', label: 'Structure', to: '/help/structure', icon: ListTree, tier: 2, helpDoc: 'help/structure' },
      { id: 'help-nav', label: 'Navigation & tabs', to: '/help/navigation', icon: LayoutGrid, tier: 2, helpDoc: 'help/navigation' },
      // Tier 3 — the demo's 17-stop guided tour is a roadmap row, so this row
      // is invisible while TIER_MAX is 2. It exists so the roadmap has a slot.
      { id: 'help-tour', label: 'Guided tour', to: '/help', icon: Flag, tier: 2, helpDoc: 'help/tour' },
      { id: 'help-roles', label: 'Who uses what', to: '/help/roles', icon: Users, tier: 2, helpDoc: 'help/roles' },
    ],
  },
];

// -------------------------------------------------------------------------
// Badges
// -------------------------------------------------------------------------

/**
 * The count pill for a nav row, or `undefined` when it carries none.
 *
 * Resolution order: a literal pinned on the item wins, then the derived count
 * registered under the item's `id` in `oem-counts.ts`, then nothing. Lookup is
 * by `id` rather than a separate `badgeKey` so a row cannot drift from its
 * count — there is only one name to get right, and it is the one the palette
 * and the comment anchors already use.
 *
 * `mode` gates the pill before either lookup: in executive mode only the four
 * ids in `EXECUTIVE_BADGE_KEYS` render one, pinned literal or not.
 *
 * `counts` is a parameter so a scene holding lazy data can pass
 * `{ ...OEM_STATIC_COUNTS, ...derived }` and light the real approval and
 * job-board figures without the sidebar having to fetch anything. Nothing
 * does today: the pinned fallbacks are asserted against the shipped pack by
 * `scripts/oem/check-oem-counts.mjs`, so they are already the live numbers.
 */
export function navBadge(
  item: Pick<OemNavItem, 'id' | 'badge'>,
  counts: Readonly<Partial<OemCounts>> = OEM_STATIC_COUNTS,
  mode: DemoMode = 'full',
): number | undefined {
  // fork(P7a/A4): executive mode keeps four pills, not eleven. The rule lives
  // in oem-counts.ts beside the figures themselves, so adding a count and
  // deciding whether an executive should see it are one edit.
  if (mode === 'executive' && !showsExecutiveBadge(item.id)) return undefined;
  if (item.badge !== undefined) return item.badge;
  return hasCount(item.id) ? counts[item.id] : undefined;
}

// -------------------------------------------------------------------------
// Derived views
// -------------------------------------------------------------------------

/**
 * The sections a given mode renders, filtered to the tier ceiling.
 *
 * Sections whose items all fall away drop out entirely, so the sidebar never
 * renders an empty heading — after a `TIER_MAX` flip or after the executive
 * cut. In executive mode rows also sort by {@link EXECUTIVE_NAV_IDS} and only
 * Home and Issues stay open on first paint (A7).
 *
 * Defaults to `'full'` so every non-UI caller — the breadcrumb helpers, the
 * palette, the check scripts — keeps seeing the whole nav unless it asks not
 * to. The sidebar is the one caller that passes a mode.
 */
export function visibleSections(
  mode: DemoMode = 'full',
  tierMax: NavTier = TIER_MAX,
): OemNavSection[] {
  return OEM_SECTIONS.map((section) => {
    const items = section.items.filter((item) =>
      item.tier > tierMax
        ? false
        : mode === 'full'
          ? !item.executiveOnly
          : isExecutiveNavId(item.id),
    );
    if (mode === 'full') return { ...section, items };
    return {
      ...section,
      defaultOpen: EXECUTIVE_OPEN_SECTIONS.includes(section.id),
      items: [...items].sort(
        (a, b) => (EXECUTIVE_RANK[a.id] ?? 0) - (EXECUTIVE_RANK[b.id] ?? 0),
      ),
    };
  }).filter((section) => section.items.length > 0);
}

/** Every item a mode renders, paired with the section it belongs to. */
export function allNavItems(
  mode: DemoMode = 'full',
  tierMax: NavTier = TIER_MAX,
): { section: OemNavSection; item: OemNavItem }[] {
  return visibleSections(mode, tierMax).flatMap((section) =>
    section.items.map((item) => ({ section, item })),
  );
}

/** Exact-path lookup. Query strings are stripped before matching. */
export const NAV_BY_PATH: Record<
  string,
  { section: OemNavSection; item: OemNavItem }
> = (() => {
  const map: Record<string, { section: OemNavSection; item: OemNavItem }> = {};
  for (const section of OEM_SECTIONS) {
    for (const item of section.items) {
      // fork(P7a/A2): the two executive-only section landings are not crumb
      // owners. `/workflows` already resolves through the fallback below, and
      // claiming it here would push a "Workflows" segment into every module
      // and step crumb — a mode-dependent breadcrumb, which is worse than a
      // short one.
      if (item.executiveOnly) continue;
      const path = item.to.split('?')[0];
      // First writer wins: `/` and `/help` are each claimed by one canonical
      // row, and the sub-class rows collapse onto their division's path.
      if (!(path in map)) map[path] = { section, item };
    }
  }
  return map;
})();

/**
 * The one nav item that should read as active for a pathname, as its id.
 *
 * Plain prefix matching lights up two rows at once wherever one item's route
 * is a prefix of another's — `/issues` (Live) and `/issues/alerts` (Alerts)
 * being the case that shows. Longest match wins, so the most specific row is
 * the only one highlighted; a machine page keeps Fleet health lit because
 * `/machines/:id` has no row of its own but the crumb's parent does.
 */
export function activeNavId(
  pathname: string,
  search = '',
  mode: DemoMode = 'full',
  tierMax: NavTier = TIER_MAX,
): string | null {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  const full = `${path}${search}`;
  let best: { id: string; length: number } | null = null;

  for (const { item } of allNavItems(mode, tierMax)) {
    // A row that carries a query (the asset sub-class rows) only matches when
    // the query matches too, and wins outright over its division's bare row.
    if (item.to.includes('?')) {
      if (item.to === full) return item.id;
      continue;
    }
    const to = item.to;
    const matches = to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`);
    if (!matches) continue;
    if (!best || to.length > best.length) best = { id: item.id, length: to.length };
  }

  if (best) return best.id;

  // Entity routes have no row; fall back to the row their crumb parent owns.
  for (const rule of DYNAMIC_CRUMB) {
    if (!rule.test.test(path)) continue;
    const parent = NAV_BY_PATH[rule.parent];
    if (parent) return parent.item.id;
  }
  return null;
}

/**
 * fork(P3c): `/sites/KG` → `Kalbarri Gold`. An unknown code falls back to the
 * raw segment so a mistyped URL still renders a crumb rather than a blank.
 */
function siteCrumbLabel(segment: string): string {
  const code = decodeURIComponent(segment).toUpperCase();
  return (MINES as Record<string, { name: string } | undefined>)[code]?.name ?? segment;
}

/**
 * fork(P5a): `/workflows/modules/parts` → `07 Parts & Supply`.
 */
function moduleCrumbLabel(segment: string): string {
  const id = decodeURIComponent(segment);
  const module = MODULE_BY_ID[id as keyof typeof MODULE_BY_ID];
  return module ? `${String(module.n).padStart(2, '0')} ${module.name}` : segment;
}

/** fork(P5a): `/workflows/steps/rca` → `Root cause analysis`. */
function stepCrumbLabel(segment: string): string {
  const id = decodeURIComponent(segment);
  const step = WORKFLOW_STEP_BY_ID[id as keyof typeof WORKFLOW_STEP_BY_ID];
  return step ? step.name : segment;
}

/**
 * fork(P5d): `/assets/loadhaul` → `Underground Load & Haul`. The three
 * division rows in the sidebar carry the same labels, but a bare
 * `/assets/<id>` reached from the Assets landing has no row of its own once
 * the query string is dropped, so the crumb resolves the label here.
 */
function divisionCrumbLabel(segment: string): string {
  const key = decodeURIComponent(segment).toLowerCase();
  return (
    ({
      surface: 'Surface Drilling',
      drilling: 'Underground Drilling',
      loadhaul: 'Underground Load & Haul',
    } as Record<string, string | undefined>)[key] ?? segment
  );
}

/** Human labels for dynamic segments the nav has no static row for. */
const DYNAMIC_CRUMB: { test: RegExp; label: (m: RegExpMatchArray) => string; parent: string }[] = [
  { test: /^\/machines\/([^/]+)$/, label: (m) => decodeURIComponent(m[1]), parent: '/fleet' },
  // fork(P5d): a division page's crumb reads the division's screen label.
  { test: /^\/assets\/([^/]+)$/, label: (m) => divisionCrumbLabel(m[1]), parent: '/assets' },
  // fork(P3c): a site page's crumb reads the site's display name, not its code.
  { test: /^\/sites\/([^/]+)$/, label: (m) => siteCrumbLabel(m[1]), parent: '/sites' },
  { test: /^\/issues\/alerts\/([^/]+)$/, label: (m) => decodeURIComponent(m[1]), parent: '/issues/alerts' },
  { test: /^\/issues\/incidents\/([^/]+)$/, label: (m) => decodeURIComponent(m[1]), parent: '/issues/incidents' },
  // fork(P5b): the loops section landing and any loop id the nav has no row
  // for. The five shipped loops each have their own nav row, so they resolve
  // through `NAV_BY_PATH` above and never reach these rules; `/loops` and a
  // mistyped id do. `parent: '/loops'` is deliberately not a nav path — there
  // is no row for the landing, so the crumb reads Home › <label> rather than
  // borrowing the first loop's row and claiming a section the page is not in.
  { test: /^\/loops$/, label: () => 'Feedback loops', parent: '/loops' },
  { test: /^\/loops\/([^/]+)$/, label: (m) => loopCrumbLabel(m[1]), parent: '/loops' },
  // fork(P5a): a module crumb reads `01 Asset Base`, a step crumb its name.
  // Both fall back to the raw segment, so a mistyped id still renders a crumb.
  {
    test: /^\/workflows\/modules\/([^/]+)$/,
    label: (m) => moduleCrumbLabel(m[1]),
    parent: '/workflows',
  },
  {
    test: /^\/workflows\/steps\/([^/]+)$/,
    label: (m) => stepCrumbLabel(m[1]),
    parent: '/workflows',
  },
  // fork(L3): a generated report's crumb reads what it is and for whom.
  {
    test: /^\/reports\/(weekly|email|deck)\/([^/]+)$/,
    label: (m) => reportCrumbLabel(m[1], m[2]),
    parent: '/reports',
  },
];

/** fork(L3): `/reports/email/MI` → `Risk email · Mount Isolde`. */
function reportCrumbLabel(kind: string, segment: string): string {
  const key = decodeURIComponent(segment);
  if (kind === 'weekly') {
    return isWeeklyKind(key) ? `Weekly status · ${WEEKLY_DIVISION[key]}` : key;
  }
  return `${kind === 'email' ? 'Risk email' : 'Equipment summary'} · ${siteCrumbLabel(key)}`;
}

/** fork(P5b): `/loops/component` → `Component Lifecycle & Rebuild`. */
function loopCrumbLabel(segment: string): string {
  const id = decodeURIComponent(segment);
  return (LOOP_BY_ID as Record<string, { name: string } | undefined>)[id]?.name ?? id;
}

/**
 * fork(qa2.Q2): `/assets/<division>?subclass=<value>` → the sidebar's own row
 * label for that sub-class, keyed `<path>\0<decoded subclass>`.
 *
 * The six sub-class rows are the only nav rows whose identity lives in the
 * query string — every other route is fully described by its path, which is
 * why `crumbFor` drops the query everywhere else. Building the table from the
 * rows themselves means the crumb leaf can never drift from the sidebar label,
 * and adding a fourth sub-class is still one `OEM_SECTIONS` entry.
 */
const SUBCLASS_CRUMB_LABEL: Readonly<Record<string, string>> = (() => {
  const map: Record<string, string> = {};
  for (const section of OEM_SECTIONS) {
    for (const item of section.items) {
      const [path, query] = item.to.split('?');
      if (!query || !path.startsWith('/assets/')) continue;
      const subclass = new URLSearchParams(query).get('subclass');
      if (subclass) map[`${path}\u0000${subclass}`] = item.label;
    }
  }
  return map;
})();

/**
 * The sub-class an assets URL selects, as the sidebar labels it, or `null`.
 *
 * Null for every non-assets route, for an assets route with no query, and for
 * a sub-class no row claims — so a hand-typed `?subclass=Nope` leaves the
 * division crumb exactly as it was rather than inventing a segment.
 * `URLSearchParams` decodes, so `?subclass=Haulage%20trucks` and
 * `?subclass=Haulage+trucks` both resolve.
 */
function subclassCrumbLabel(path: string, search: string): string | null {
  if (!search || !path.startsWith('/assets/')) return null;
  const subclass = new URLSearchParams(search).get('subclass');
  if (!subclass) return null;
  return SUBCLASS_CRUMB_LABEL[`${path}\u0000${subclass}`] ?? null;
}

/**
 * Breadcrumb for a pathname: Home › Section › View, with a fourth segment for
 * entity routes (`/machines/L-02`). Feeds `chrome/Breadcrumb.tsx` unchanged.
 *
 * `search` is optional and consulted for the assets routes only (fork(qa2.Q2)):
 * a sub-class page is a division path plus `?subclass=`, so without it the
 * crumb read `Assets › Underground Load & Haul` while the heading read
 * `… › Loaders`. Every other route ignores it and is unchanged, including a
 * call that omits it entirely.
 */
export function crumbFor(pathname: string, search = ''): BreadcrumbSegment[] {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  const home: BreadcrumbSegment = { label: 'Home', hash: '/' };

  if (path === '/') return [home];

  // fork(qa2.Q2): the sub-class leaf, when the query names one. Empty for
  // every other route, so the spreads below are no-ops off the assets pages.
  const subclass = subclassCrumbLabel(path, search);
  const leaf: BreadcrumbSegment[] = subclass
    ? [{ label: subclass, hash: `${path}${search}` }]
    : [];

  const direct = NAV_BY_PATH[path];
  if (direct) {
    return dedupe([
      home,
      { label: direct.section.label, hash: direct.item.to, static: true },
      { label: direct.item.label, hash: direct.item.to },
      ...leaf,
    ]);
  }

  for (const rule of DYNAMIC_CRUMB) {
    const m = path.match(rule.test);
    if (!m) continue;
    const parent = NAV_BY_PATH[rule.parent];
    return dedupe([
      home,
      ...(parent
        ? [
            { label: parent.section.label, hash: parent.item.to, static: true },
            { label: parent.item.label, hash: parent.item.to },
          ]
        : []),
      { label: rule.label(m), hash: path },
      ...leaf,
    ]);
  }

  // Unknown route (foundation leftovers, /settings, /admin): fall back to a
  // title-cased last segment so the crumb bar never renders empty.
  const last = path.split('/').filter(Boolean).pop() ?? '';
  const label = last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
  return dedupe([home, { label, hash: path }]);
}

/**
 * Collapse a repeated label. The `home` section is literally called "Home", so
 * every row inside it would otherwise render "Home › Home › Cost summary".
 */
function dedupe(segments: BreadcrumbSegment[]): BreadcrumbSegment[] {
  return segments.filter(
    (seg, i) => i === 0 || seg.label !== segments[i - 1].label,
  );
}
